import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaUpload, FaDownload, FaSearch, FaFilter, FaTrash, FaCheckCircle, FaTimesCircle, FaPlus, FaSync, FaFileExcel, FaTimes, FaSlidersH } from 'react-icons/fa';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useUI } from '../contexts/UIContext';
import { getServerURL } from '../config/api';
import * as XLSX from 'xlsx';
import '../styles/shared.css';
import './ScanComparison.css';

// LocalStorage keys
const STORAGE_KEYS = {
    EXCEL_DATA: 'robridge_excel_data',
    EXCEL_HEADERS: 'robridge_excel_headers',
    EXCEL_METADATA: 'robridge_excel_metadata',
    SCAN_HISTORY: 'robridge_scan_history',
    SCANNED_CODES: 'robridge_scanned_codes',
    UNKNOWN_SCANS: 'robridge_unknown_scans'
};

const ScanComparison = () => {
    const { showToast, showConfirm } = useUI();
    // Excel data state
    const [excelData, setExcelData] = useState([]);
    const [excelHeaders, setExcelHeaders] = useState([]);
    const [excelMetadata, setExcelMetadata] = useState(null);

    // Scanned data state
    const [scannedCodes, setScannedCodes] = useState(new Set());
    const [unknownScans, setUnknownScans] = useState([]);
    const [scanHistory, setScanHistory] = useState([]);

    // UI state
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortOrder, setSortOrder] = useState('id');

    // Advanced filtering state
    const [activeFilters, setActiveFilters] = useState({}); // { columnName: selectedValue }
    const [showFilterPanel, setShowFilterPanel] = useState(false);

    const { socket } = useWebSocket();

    // Refs to always access latest values inside WebSocket callback (avoids stale closure)
    const excelDataRef = useRef(excelData);
    const excelMetadataRef = useRef(excelMetadata);

    // Keep refs in sync with state
    useEffect(() => {
        excelDataRef.current = excelData;
    }, [excelData]);

    useEffect(() => {
        excelMetadataRef.current = excelMetadata;
    }, [excelMetadata]);

    // Load data from localStorage on mount and sync with server recent scans
    useEffect(() => {
        const loadAndSyncData = async () => {
            try {
                const savedData = localStorage.getItem(STORAGE_KEYS.EXCEL_DATA);
                const savedHeaders = localStorage.getItem(STORAGE_KEYS.EXCEL_HEADERS);
                const savedMetadata = localStorage.getItem(STORAGE_KEYS.EXCEL_METADATA);
                const savedScanHistory = localStorage.getItem(STORAGE_KEYS.SCAN_HISTORY);
                const savedScannedCodes = localStorage.getItem(STORAGE_KEYS.SCANNED_CODES);
                const savedUnknownScans = localStorage.getItem(STORAGE_KEYS.UNKNOWN_SCANS);

                const parsedData = savedData ? JSON.parse(savedData) : [];
                const parsedMetadata = savedMetadata ? JSON.parse(savedMetadata) : null;
                const parsedUnknownScans = savedUnknownScans ? JSON.parse(savedUnknownScans) : [];
                const parsedScannedCodes = savedScannedCodes ? new Set(JSON.parse(savedScannedCodes)) : new Set();
                let currentScanHistory = savedScanHistory ? JSON.parse(savedScanHistory) : [];

                if (savedData) setExcelData(parsedData);
                if (savedHeaders) setExcelHeaders(JSON.parse(savedHeaders));
                if (savedMetadata) setExcelMetadata(parsedMetadata);

                // Fetch recent scans from the server to catch any missed while navigating away
                try {
                    const token = localStorage.getItem('robridge_token');
                    if (token) {
                        const serverURL = getServerURL();
                        const res = await fetch(`${serverURL}/api/barcodes/scanned?limit=150`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        const data = await res.json();
                        if (data.success && data.barcodes) {
                            const serverScans = data.barcodes.map(b => ({
                                code: b.barcode_data?.toString().trim(),
                                timestamp: b.created_at,
                                deviceId: b.device_id || 'Unknown'
                            })).filter(s => s.code);

                            // Merge them (only add server scans that are newer than our newest local scan)
                            if (currentScanHistory.length > 0) {
                                const newestLocalTime = new Date(currentScanHistory[0].timestamp).getTime();
                                const newServerScans = serverScans.filter(s => new Date(s.timestamp).getTime() > newestLocalTime);
                                currentScanHistory = [...newServerScans, ...currentScanHistory];
                            } else {
                                currentScanHistory = serverScans;
                            }
                        }
                    }
                } catch (e) { console.error('Error fetching recent scans for sync', e); }

                setScanHistory(currentScanHistory);

                // Re-evaluate EVERYTHING based on the complete currentScanHistory
                if (parsedData.length > 0 && parsedMetadata?.idColumn) {
                    const idColumn = parsedMetadata.idColumn;
                    const excelIds = new Set(parsedData.map(row => row[idColumn]?.toString().trim()));

                    const newScannedCodes = new Set();
                    const newUnknownScans = [];
                    const unknownSet = new Set();

                    currentScanHistory.forEach(scan => {
                        if (excelIds.has(scan.code)) {
                            newScannedCodes.add(scan.code);
                        } else {
                            if (!unknownSet.has(scan.code)) {
                                unknownSet.add(scan.code);
                                newUnknownScans.push(scan);
                            }
                        }
                    });

                    setScannedCodes(newScannedCodes);
                    setUnknownScans(newUnknownScans);
                } else {
                    setScannedCodes(parsedScannedCodes);
                    setUnknownScans(parsedUnknownScans);
                }
            } catch (error) {
                console.error('Error loading data from localStorage:', error);
            }
        };

        loadAndSyncData();
    }, []);

    // Save data to localStorage whenever it changes (debounced)
    useEffect(() => {
        const timer = setTimeout(() => {
            try {
                localStorage.setItem(STORAGE_KEYS.EXCEL_DATA, JSON.stringify(excelData));
                localStorage.setItem(STORAGE_KEYS.EXCEL_HEADERS, JSON.stringify(excelHeaders));
                if (excelMetadata) {
                    localStorage.setItem(STORAGE_KEYS.EXCEL_METADATA, JSON.stringify(excelMetadata));
                }
                localStorage.setItem(STORAGE_KEYS.SCAN_HISTORY, JSON.stringify(scanHistory));
                localStorage.setItem(STORAGE_KEYS.SCANNED_CODES, JSON.stringify(Array.from(scannedCodes)));
                localStorage.setItem(STORAGE_KEYS.UNKNOWN_SCANS, JSON.stringify(unknownScans));
            } catch (error) {
                console.error('Error saving to localStorage:', error);
                if (error.name === 'QuotaExceededError') {
                    showToast('Storage limit exceeded! Consider exporting your data and starting fresh.', 'warning');
                }
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [excelData, excelHeaders, excelMetadata, scanHistory, scannedCodes, unknownScans]);

    // Listen for real-time scans via WebSocket
    // Uses refs to avoid stale closure over excelData/excelMetadata
    useEffect(() => {
        if (socket) {
            const handleNewScan = (scanData) => {
                const code = scanData.barcodeData?.toString().trim();
                if (!code) return;

                const timestamp = new Date().toISOString();

                setScanHistory(prev => [{
                    code,
                    timestamp,
                    deviceId: scanData.deviceId || 'Unknown'
                }, ...prev]);

                // Use refs to always read the latest excelData/excelMetadata
                const currentExcelData = excelDataRef.current;
                const currentMetadata = excelMetadataRef.current;
                const idColumn = currentMetadata?.idColumn;

                const existsInExcel = idColumn
                    ? currentExcelData.some(row => row[idColumn]?.toString().trim() === code)
                    : false;

                if (existsInExcel) {
                    setScannedCodes(prev => new Set([...prev, code]));
                } else {
                    setUnknownScans(prev => {
                        const exists = prev.some(scan => scan.code === code);
                        if (!exists) {
                            return [...prev, { code, timestamp, deviceId: scanData.deviceId || 'Unknown' }];
                        }
                        return prev;
                    });
                }
            };

            socket.on('esp32_barcode_scan', handleNewScan);
            return () => socket.off('esp32_barcode_scan', handleNewScan);
        }
    }, [socket]); // Only depends on socket - refs handle fresh data access

    // Parse Excel file
    const parseExcelFile = (file, mode = 'replace') => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (jsonData.length < 2) {
                    showToast('Excel file must have at least a header row and one data row!', 'warning');
                    return;
                }

                const headers = jsonData[0].map(h => h?.toString().trim());
                const headersLower = headers.map(h => h.toLowerCase());

                let idColumnIndex = headersLower.findIndex(h =>
                    h.includes('roll') || h.includes('id') || h.includes('code') || h.includes('number') || h.includes('sku')
                );

                // If no specific ID column found, use the first column
                if (idColumnIndex === -1) {
                    idColumnIndex = 0;
                }



                const idColumnName = headers[idColumnIndex];

                const parsedData = jsonData.slice(1)
                    .filter(row => row[idColumnIndex])
                    .map(row => {
                        const rowData = {};
                        headers.forEach((header, index) => {
                            rowData[header] = row[index]?.toString().trim() || '';
                        });
                        return rowData;
                    });

                if (mode === 'replace') {
                    setExcelData(parsedData);
                    setExcelHeaders(headers);
                    setExcelMetadata({
                        fileName: file.name,
                        uploadDate: new Date().toISOString(),
                        idColumn: idColumnName,
                        totalRows: parsedData.length
                    });
                    setScannedCodes(new Set());
                    setUnknownScans([]);
                    setScanHistory([]);
                    setActiveFilters({});
                    showToast(`Successfully loaded ${parsedData.length} entries from Excel!`, 'success');
                } else if (mode === 'merge') {
                    // Validate column structure compatibility
                    const existingHeaders = excelHeaders;
                    const newHeaders = headers;

                    // Calculate how many existing columns are present in the new file
                    const matchingColumns = existingHeaders.filter(existingHeader =>
                        newHeaders.some(newHeader =>
                            newHeader.toLowerCase().trim() === existingHeader.toLowerCase().trim()
                        )
                    );

                    // Calculate match percentage
                    const matchPercentage = (matchingColumns.length / existingHeaders.length) * 100;

                    // If less than 50% of existing columns match, it's a different template
                    if (matchPercentage < 50) {
                        showToast(
                            `Template Mismatch!\n\n` +
                            `The uploaded Excel file has a different structure and cannot be used to update the existing data.\n\n` +
                            `Existing columns: ${existingHeaders.join(', ')}\n\n` +
                            `New file columns: ${newHeaders.join(', ')}\n\n` +
                            `Only ${matchingColumns.length} out of ${existingHeaders.length} columns match (${matchPercentage.toFixed(0)}%).\n\n` +
                            `To update the data, please use an Excel file with the same or similar column structure.`, 'error'
                        );
                        return;
                    }

                    const existingData = [...excelData];
                    let updatedCount = 0;
                    let addedCount = 0;

                    parsedData.forEach(newRow => {
                        const newId = newRow[idColumnName];
                        const existingIndex = existingData.findIndex(row => row[idColumnName] === newId);

                        if (existingIndex !== -1) {
                            existingData[existingIndex] = newRow;
                            updatedCount++;
                        } else {
                            existingData.push(newRow);
                            addedCount++;
                        }
                    });

                    setExcelData(existingData);
                    setExcelHeaders(headers);
                    setExcelMetadata(prev => ({
                        ...prev,
                        lastUpdateDate: new Date().toISOString(),
                        totalRows: existingData.length
                    }));
                    showToast(`Merged successfully!\n\n• Updated: ${updatedCount} entries\n• Added: ${addedCount} new entries\n• Total: ${existingData.length} entries`, 'success');
                }
            } catch (error) {
                console.error('Error parsing Excel:', error);
                showToast('Error parsing Excel file. Please ensure it is a valid .xlsx or .xls file.', 'error');
            }
        };

        reader.readAsArrayBuffer(file);
    };

    const handleAddFile = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (excelData.length > 0) {
            showConfirm('Replace Data', '⚠️ This will replace all existing data. Continue?', () => {
                parseExcelFile(file, 'replace');
            });
            event.target.value = '';
            return;
        }

        parseExcelFile(file, 'replace');
        event.target.value = '';
    };

    const handleUpdateFile = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (excelData.length === 0) {
            showToast('No existing data to merge with. Please use "Add New File" instead.', 'warning');
            event.target.value = '';
            return;
        }

        parseExcelFile(file, 'merge');
        event.target.value = '';
    };

    const handleDeleteAll = () => {
        showConfirm('Delete All Data', '⚠️ Are you sure you want to delete ALL data? This cannot be undone!', () => {
            setExcelData([]);
            setExcelHeaders([]);
            setExcelMetadata(null);
            setScannedCodes(new Set());
            setUnknownScans([]);
            setScanHistory([]);
            setActiveFilters({});

            Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));

            showToast('All data cleared successfully!', 'success');
        });
    };

    const exportToCSV = () => {
        if (excelData.length === 0) {
            showToast('No data to export!', 'warning');
            return;
        }

        const idColumn = excelMetadata?.idColumn || excelHeaders[0];

        const csvRows = [
            ['Status', ...excelHeaders, 'Scanned Time'],
            ...filteredData.map(row => {
                const id = row[idColumn];
                const isScanned = scannedCodes.has(id);
                const scanTime = scanHistory.find(s => s.code === id)?.timestamp || '';
                return [
                    isScanned ? 'Scanned' : 'Pending',
                    ...excelHeaders.map(header => row[header] || ''),
                    scanTime ? new Date(scanTime).toLocaleString() : ''
                ];
            }),
            [],
            ['Unknown Scans (Not in Excel)'],
            ['Code', 'Timestamp', 'Device ID'],
            ...unknownScans.map(scan => [
                scan.code,
                new Date(scan.timestamp).toLocaleString(),
                scan.deviceId
            ])
        ];

        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `scan_comparison_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    // Get filterable columns
    const filterableColumns = useMemo(() => {
        if (excelData.length === 0 || !excelMetadata) return [];

        const idColumn = excelMetadata.idColumn;
        const columns = [];

        excelHeaders.forEach(header => {
            if (header === idColumn) return;

            const values = excelData.map(row => row[header]).filter(Boolean);
            const uniqueValues = [...new Set(values)];

            if (uniqueValues.length >= 2 && uniqueValues.length <= 50 && uniqueValues.length < excelData.length * 0.8) {
                columns.push({
                    name: header,
                    options: uniqueValues.sort()
                });
            }
        });

        return columns;
    }, [excelData, excelHeaders, excelMetadata]);

    // Handle filter changes
    const handleFilterChange = (columnName, value) => {
        setActiveFilters(prev => {
            const newFilters = { ...prev };
            if (value === 'all') {
                delete newFilters[columnName];
            } else {
                newFilters[columnName] = value;
            }
            return newFilters;
        });
    };

    // Clear all filters
    const clearAllFilters = () => {
        setActiveFilters({});
        setFilterStatus('all');
        setSearchTerm('');
    };

    // Analyze categorical columns for dynamic dashboard
    const categoricalAnalysis = useMemo(() => {
        if (excelData.length === 0 || !excelMetadata) return [];

        const idColumn = excelMetadata.idColumn;
        const analyses = [];

        excelHeaders.forEach(header => {
            if (header === idColumn) return;

            const values = excelData.map(row => row[header]).filter(Boolean);
            const uniqueValues = [...new Set(values)];

            if (uniqueValues.length >= 2 && uniqueValues.length <= 20 && uniqueValues.length < excelData.length * 0.5) {
                const groups = uniqueValues.map(value => {
                    const total = excelData.filter(row => row[header] === value).length;
                    const scanned = excelData.filter(row =>
                        row[header] === value && scannedCodes.has(row[idColumn])
                    ).length;
                    const percentage = total > 0 ? Math.round((scanned / total) * 100) : 0;

                    return { value, total, scanned, percentage };
                });

                analyses.push({ column: header, groups });
            }
        });

        return analyses;
    }, [excelData, excelHeaders, excelMetadata, scannedCodes]);

    // Must be defined BEFORE the stats useMemo that calls it
    const calculateScanRate = () => {
        if (scanHistory.length < 2) return 0;

        const firstScan = new Date(scanHistory[scanHistory.length - 1].timestamp);
        const lastScan = new Date(scanHistory[0].timestamp);
        const minutesDiff = (lastScan - firstScan) / 1000 / 60;

        return minutesDiff > 0 ? Math.round(scanHistory.length / minutesDiff) : 0;
    };

    // Calculate statistics
    const stats = useMemo(() => {
        const totalInExcel = excelData.length;
        const idColumn = excelMetadata?.idColumn;
        const totalScanned = idColumn ? Array.from(scannedCodes).filter(code =>
            excelData.some(row => row[idColumn] === code)
        ).length : 0;
        const totalPending = totalInExcel - totalScanned;
        const totalUnknown = unknownScans.length;
        const scanRate = scanHistory.length > 0 ? calculateScanRate() : 0;

        return { totalInExcel, totalScanned, totalPending, totalUnknown, scanRate };
    }, [excelData, scannedCodes, unknownScans, scanHistory, excelMetadata]);

    // Filter and sort data with advanced filters
    const filteredData = useMemo(() => {
        if (!excelMetadata) return [];

        const idColumn = excelMetadata.idColumn;

        return excelData
            .filter(row => {
                const id = row[idColumn];

                const matchesSearch = searchTerm === '' ||
                    Object.values(row).some(val =>
                        val?.toString().toLowerCase().includes(searchTerm.toLowerCase())
                    );

                const matchesStatus =
                    filterStatus === 'all' ||
                    (filterStatus === 'scanned' && scannedCodes.has(id)) ||
                    (filterStatus === 'pending' && !scannedCodes.has(id));

                const matchesAdvancedFilters = Object.entries(activeFilters).every(([column, value]) => {
                    return row[column]?.toString() === value;
                });

                return matchesSearch && matchesStatus && matchesAdvancedFilters;
            })
            .sort((a, b) => {
                const idColumn = excelMetadata.idColumn;
                if (sortOrder === 'id') {
                    return a[idColumn]?.localeCompare(b[idColumn]);
                } else if (sortOrder === 'status') {
                    const aScanned = scannedCodes.has(a[idColumn]);
                    const bScanned = scannedCodes.has(b[idColumn]);
                    return bScanned - aScanned;
                }
                return 0;
            });
    }, [excelData, excelMetadata, searchTerm, filterStatus, sortOrder, scannedCodes, activeFilters]);

    const activeFilterCount = Object.keys(activeFilters).length + (filterStatus !== 'all' ? 1 : 0);

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="page-header-content">
                    <div className="page-header-text">
                        <h1>Scan Comparison Dashboard</h1>
                        <p>Universal analytics for attendance, inventory, events & logistics</p>
                    </div>
                </div>
            </div>

            {/* File Management Section */}
            <div className="file-management-section">
                <div className="file-info-card">
                    {excelMetadata ? (
                        <>
                            <div className="file-icon-wrapper">
                                <FaFileExcel className="file-icon" />
                            </div>
                            <div className="file-details">
                                <h3>{excelMetadata.fileName}</h3>
                                <div className="file-meta">
                                    <span>📅 {new Date(excelMetadata.uploadDate).toLocaleDateString()}</span>
                                    <span>•</span>
                                    <span className="file-stats">{excelMetadata.totalRows} entries</span>
                                    <span>•</span>
                                    <span>{excelHeaders.length} columns</span>
                                </div>
                                {excelMetadata.lastUpdateDate && (
                                    <p className="update-info">Last updated: {new Date(excelMetadata.lastUpdateDate).toLocaleString()}</p>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="file-icon-wrapper empty">
                                <FaUpload className="file-icon" />
                            </div>
                            <div className="file-details">
                                <h3>No File Uploaded</h3>
                                <p>Upload an Excel file to get started with real-time tracking</p>
                            </div>
                        </>
                    )}
                </div>

                <div className="file-actions">
                    <input type="file" id="add-file" accept=".xlsx,.xls" onChange={handleAddFile} style={{ display: 'none' }} />
                    <label htmlFor="add-file" className="btn btn-primary">
                        <FaPlus /> Add New
                    </label>

                    <input type="file" id="update-file" accept=".xlsx,.xls" onChange={handleUpdateFile} style={{ display: 'none' }} disabled={excelData.length === 0} />
                    <label htmlFor="update-file" className={`btn btn-success ${excelData.length === 0 ? 'disabled' : ''}`}>
                        <FaSync /> Update
                    </label>

                    <button className="btn btn-danger" onClick={handleDeleteAll} disabled={excelData.length === 0}>
                        <FaTrash /> Delete
                    </button>
                </div>
            </div>

            {excelData.length > 0 && (
                <>
                    {/* Statistics Cards */}
                    <div className="stats-section">
                        <div className="stat-card total">
                            <div className="stat-background"></div>
                            <div className="stat-content">
                                <div className="stat-icon">📊</div>
                                <div className="stat-info">
                                    <h3>{stats.totalInExcel}</h3>
                                    <p>Total Entries</p>
                                </div>
                            </div>
                        </div>
                        <div className="stat-card scanned">
                            <div className="stat-background"></div>
                            <div className="stat-content">
                                <div className="stat-icon">✅</div>
                                <div className="stat-info">
                                    <h3>{stats.totalScanned}</h3>
                                    <p>Scanned</p>
                                    <span className="stat-percentage">{stats.totalInExcel > 0 ? Math.round((stats.totalScanned / stats.totalInExcel) * 100) : 0}%</span>
                                </div>
                            </div>
                        </div>
                        <div className="stat-card pending">
                            <div className="stat-background"></div>
                            <div className="stat-content">
                                <div className="stat-icon">⏳</div>
                                <div className="stat-info">
                                    <h3>{stats.totalPending}</h3>
                                    <p>Pending</p>
                                </div>
                            </div>
                        </div>
                        <div className="stat-card unknown">
                            <div className="stat-background"></div>
                            <div className="stat-content">
                                <div className="stat-icon">❓</div>
                                <div className="stat-info">
                                    <h3>{stats.totalUnknown}</h3>
                                    <p>Unknown</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dynamic Analysis Dashboard */}
                    {categoricalAnalysis.length > 0 && (
                        <div className="analysis-dashboard">
                            <div className="section-header">
                                <h2>📈 Detailed Analysis</h2>
                                <p>Auto-generated insights from your data</p>
                            </div>
                            <div className="analysis-grid">
                                {categoricalAnalysis.map((analysis, idx) => (
                                    <div key={idx} className="analysis-card">
                                        <div className="card-header">
                                            <h3>{analysis.column}</h3>
                                            <span className="card-badge">{analysis.groups.length} groups</span>
                                        </div>
                                        <div className="analysis-groups">
                                            {analysis.groups.map((group, gIdx) => (
                                                <div key={gIdx} className="group-item">
                                                    <div className="group-header">
                                                        <span className="group-name">{group.value}</span>
                                                        <span className="group-stats">
                                                            {group.scanned}/{group.total}
                                                        </span>
                                                    </div>
                                                    <div className="progress-bar-container">
                                                        <div className="progress-bar">
                                                            <div
                                                                className={`progress-fill ${group.percentage >= 80 ? 'high' :
                                                                    group.percentage >= 50 ? 'medium' : 'low'
                                                                    }`}
                                                                style={{ width: `${group.percentage}%` }}
                                                            >
                                                                <span className="progress-text">{group.percentage}%</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Advanced Filters & Controls */}
                    <div className="controls-section">
                        <div className="controls-top-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center', justifyContent: 'space-between' }}>
                            <button
                                className="btn btn-filter-toggle"
                                onClick={() => setShowFilterPanel(!showFilterPanel)}
                                style={{ margin: 0 }}
                            >
                                <FaSlidersH /> Filter
                                {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
                            </button>

                            <div className="search-filter-container" style={{ margin: 0, flex: 1, display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                <div className="search-input" style={{ flex: 1, minWidth: '200px', maxWidth: '400px' }}>
                                    <FaSearch className="search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="filter-dropdown">
                                    <FaFilter className="filter-icon" />
                                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                                        <option value="all">All Status</option>
                                        <option value="scanned">Scanned Only</option>
                                        <option value="pending">Pending Only</option>
                                    </select>
                                </div>
                                <div className="filter-dropdown">
                                    <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                                        <option value="id">Sort by ID</option>
                                        <option value="status">Sort by Status</option>
                                    </select>
                                </div>
                            </div>

                            <div className="action-buttons" style={{ margin: 0, display: 'flex', gap: '16px', alignItems: 'center' }}>
                                <button className="btn btn-primary" onClick={exportToCSV}>
                                    <FaDownload /> Export CSV
                                </button>
                                <div className="results-count">
                                    Showing {filteredData.length} of {excelData.length} entries
                                </div>
                            </div>
                        </div>

                        {showFilterPanel && filterableColumns.length > 0 && (
                            <div className="filter-panel" style={{ marginTop: '24px' }}>
                                <div className="filter-panel-header">
                                    <h4>Advanced Filters</h4>
                                    {activeFilterCount > 0 && (
                                        <button className="btn-clear-filters" onClick={clearAllFilters}>
                                            <FaTimes /> Clear All
                                        </button>
                                    )}
                                </div>
                                <div className="filter-grid">
                                    {filterableColumns.map((column, idx) => (
                                        <div key={idx} className="filter-item">
                                            <label>{column.name}</label>
                                            <select
                                                value={activeFilters[column.name] || 'all'}
                                                onChange={(e) => handleFilterChange(column.name, e.target.value)}
                                                className="filter-select"
                                            >
                                                <option value="all">All {column.name}</option>
                                                {column.options.map((option, oIdx) => (
                                                    <option key={oIdx} value={option}>{option}</option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Data Table */}
                    <div className="data-table-container">
                        <div className="table-header">
                            <h2>📋 Data Table</h2>
                        </div>
                        <div className="table-wrapper">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th className="sticky-col">Status</th>
                                        {excelHeaders.map((header, idx) => (
                                            <th key={idx}>{header}</th>
                                        ))}
                                        <th>Scanned Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.map((row, index) => {
                                        const id = row[excelMetadata.idColumn];
                                        const isScanned = scannedCodes.has(id);
                                        const scanTime = scanHistory.find(s => s.code === id)?.timestamp;
                                        return (
                                            <tr key={index} className={isScanned ? 'scanned-row' : 'pending-row'}>
                                                <td className="status-cell sticky-col">
                                                    {isScanned ? (
                                                        <span className="status-badge scanned">
                                                            <FaCheckCircle /> Scanned
                                                        </span>
                                                    ) : (
                                                        <span className="status-badge pending">
                                                            <FaTimesCircle /> Pending
                                                        </span>
                                                    )}
                                                </td>
                                                {excelHeaders.map((header, hIdx) => (
                                                    <td key={hIdx}>{row[header] || '-'}</td>
                                                ))}
                                                <td className="time-cell">
                                                    {scanTime ? new Date(scanTime).toLocaleString() : '-'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Unknown Scans */}
                    {unknownScans.length > 0 && (
                        <div className="unknown-scans-section">
                            <h2>❓ Unknown Scans - {unknownScans.length}</h2>
                            <p className="section-subtitle">These codes were scanned but not found in your Excel file</p>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Code</th>
                                        <th>Scanned Time</th>
                                        <th>Device ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unknownScans.map((scan, index) => (
                                        <tr key={index} className="unknown-row">
                                            <td className="code-cell">{scan.code}</td>
                                            <td className="time-cell">{new Date(scan.timestamp).toLocaleString()}</td>
                                            <td className="device-cell">{scan.deviceId}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {/* Empty State */}
            {excelData.length === 0 && (
                <div className="empty-state">
                    <div className="empty-animation">
                        <div className="empty-icon">📋</div>
                        <div className="pulse-ring"></div>
                    </div>
                    <h3>No Data Loaded</h3>
                    <p>Upload an Excel file to start tracking scans and analyzing data in real-time.</p>
                    <div className="empty-features">
                        <div className="feature-item">
                            <span className="feature-icon">🎓</span>
                            <span>Student Attendance</span>
                        </div>
                        <div className="feature-item">
                            <span className="feature-icon">📦</span>
                            <span>Inventory Audits</span>
                        </div>
                        <div className="feature-item">
                            <span className="feature-icon">🎫</span>
                            <span>Event Check-ins</span>
                        </div>
                        <div className="feature-item">
                            <span className="feature-icon">🏭</span>
                            <span>Production Tracking</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScanComparison;
